import React, { useEffect, useState, useRef, useCallback } from "react";
import Button from "@/components/Base/Button";
import FormInput from "@/components/Base/Form/FormInput";
import FormSelect from "@/components/Base/Form/FormSelect";
import FormTextarea from "@/components/Base/Form/FormTextarea";
import FormSwitch from "@/components/Base/Form/FormSwitch";
import TomSelect from "@/components/Base/TomSelect";
import clsx from "clsx";
import Lucide from "@/components/Base/Lucide";
import axios from "axios";
import { getToken } from "@/utils/auth";
import { useNavigate, useParams } from "react-router-dom";
import Alert from "@/components/Base/Alert";
import Dropzone from "@/components/Base/Dropzone";

type Mode = "add" | "edit";

interface Props {
  /** Optional override. If not provided, will auto-detect from route param `id` */
  mode?: Mode;
}

interface Project {
  id: string;
  name: string;
}

interface ProductGroup {
  id: string;
  groupName: string;
  companyId: string;
}

interface ProductType {
  id: string;
  typeName: string;
  companyId: string;
}

interface PackType {
  id: string;
  packName: string;
  companyId: string;
}

interface CompetitorProduct {
  id: string;
  name: string;
}

interface FormData {
  name: string;
  description: string;
  sku: string;
  barcode: string;
  group: string;
  type: string;
  uom: string;
  packType: string;
  packDimension: string;
  grossWeightPerUOM: string;
  costSellingPrice: string;
  retailSellingPrice: string;
  dealerSellingPrice: string;
  productImageUrl: string;
  projectIds: string[];
  companyId: string;
  status: string;
  productLine: string;
  productCompetitorIds: string[];
}

interface FormErrors {
  name?: string;
  sku?: string;
  costSellingPrice?: string;
  retailSellingPrice?: string;
  dealerSellingPrice?: string;
  group?: string;
  type?: string;
  packType?: string;
}

const EMPTY_FORM: FormData = {
  name: "",
  description: "",
  sku: "",
  barcode: "",
  group: "",
  type: "",
  uom: "",
  packType: "",
  packDimension: "",
  grossWeightPerUOM: "",
  costSellingPrice: "",
  retailSellingPrice: "",
  dealerSellingPrice: "",
  productImageUrl: "",
  projectIds: [],
  companyId: "",
  status: "true",
  productLine: "Own",
  productCompetitorIds: [],
};

export default function EditProduct({ mode: modeProp }: Props) {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState<FormData>(EMPTY_FORM);
  const [errors, setErrors] = useState<FormErrors>({});
  const [projects, setProjects] = useState<Project[]>([]);
  const [productGroups, setProductGroups] = useState<ProductGroup[]>([]);
  const [productTypes, setProductTypes] = useState<ProductType[]>([]);
  const [packTypes, setPackTypes] = useState<PackType[]>([]);
  const [competitorProducts, setCompetitorProducts] = useState<CompetitorProduct[]>([]);
  const [submitError, setSubmitError] = useState("");
  const [submitSuccess, setSubmitSuccess] = useState("");
  const [imageUrl, setImageUrl] = useState<string>("");
  const [isUploadingImage, setIsUploadingImage] = useState(false);
  const [imageError, setImageError] = useState("");
  const dropzoneRef = useRef<any>();
  const fileInputRef = useRef<HTMLInputElement>(null);

  const mode = modeProp || (id ? "edit" : "add");

  useEffect(() => {
    const init = async () => {
      const apiUrl = import.meta.env.VITE_API_URL || "http://localhost:4000";
      const token = getToken();

      try {
        setLoading(true);

        const urlParams = new URLSearchParams(window.location.search);
        const companyId = urlParams.get("companyId");

        // Load projects (optional)
        try {
          const projectsUrl = companyId ? `${apiUrl}/projects?companyId=${companyId}` : `${apiUrl}/projects`;
          const projectsRes = await axios.get(projectsUrl, {
            headers: { Authorization: `Bearer ${token}` },
          });
          setProjects(projectsRes.data?.data || []);
        } catch {
          // ignore
        }

        // Load product groups
        try {
          const productGroupsUrl = companyId ? `${apiUrl}/product-groups?companyId=${companyId}` : `${apiUrl}/product-groups`;
          const groupsRes = await axios.get(productGroupsUrl, {
            headers: { Authorization: `Bearer ${token}` },
          });
          setProductGroups(groupsRes.data?.data || []);
        } catch {
          // ignore
        }

        // Load product types
        try {
          const productTypesUrl = companyId ? `${apiUrl}/product-types?companyId=${companyId}` : `${apiUrl}/product-types`;
          const typesRes = await axios.get(productTypesUrl, {
            headers: { Authorization: `Bearer ${token}` },
          });
          setProductTypes(typesRes.data?.data || []);
        } catch {
          // ignore
        }

        // Load pack types
        try {
          const packTypesUrl = companyId ? `${apiUrl}/pack-types?companyId=${companyId}` : `${apiUrl}/pack-types`;
          const packsRes = await axios.get(packTypesUrl, {
            headers: { Authorization: `Bearer ${token}` },
          });
          setPackTypes(packsRes.data?.data || []);
        } catch {
          // ignore
        }

        // Load competitor products (products where productLine is 'Competitor')
        try {
          const competitorsUrl = companyId
            ? `${apiUrl}/products?companyId=${companyId}&productLine=Competitor&pageSize=1000`
            : `${apiUrl}/products?productLine=Competitor&pageSize=1000`;
          const competitorsRes = await axios.get(competitorsUrl, {
            headers: { Authorization: `Bearer ${token}` },
          });
          const competitors = competitorsRes.data?.data || [];
          setCompetitorProducts(competitors.map((p: any) => ({ id: p.id, name: p.name })));
        } catch {
          // ignore
        }

        if (mode === "edit" && id) {
          const productRes = await axios.get(`${apiUrl}/products/${id}`, {
            headers: { Authorization: `Bearer ${token}` },
          });
          const product = productRes.data;

          const projectIds =
            product.productProjects
              ?.filter((pp: any) => pp.project && pp.projectId)
              .map((pp: any) => pp.projectId)
              .filter((pid: string) => pid) || [];

          setFormData({
            name: product.name || "",
            description: product.description || "",
            sku: product.sku || "",
            barcode: product.barcode || "",
            group: product.group || "",
            type: product.type || "",
            uom: product.uom || "",
            packType: product.packType || "",
            packDimension: product.packDimension || "",
            grossWeightPerUOM: product.grossWeightPerUOM?.toString() || "",
            costSellingPrice: product.costSellingPrice?.toString() || "",
            retailSellingPrice: product.retailSellingPrice?.toString() || "",
            dealerSellingPrice: product.dealerSellingPrice?.toString() || "",
            productImageUrl: product.productImageUrl || "",
            projectIds,
            companyId: product.companyId || "",
            status: product.status ? "true" : "false",
            productLine: product.productLine || (product.isCompetitor ? "Competitor" : "Own"),
            productCompetitorIds: product.productCompetitor || [],
          });
          const preview =
            (product as { productImagePreviewUrl?: string }).productImagePreviewUrl || product.productImageUrl || "";
          setImageUrl(preview);
        } else if (companyId) {
          setFormData((prev) => ({ ...prev, companyId }));
        }
      } catch (error) {
        console.error("Failed to initialize:", error);
        setSubmitError("Failed to load data");
      } finally {
        setLoading(false);
      }
    };

    init();
  }, [mode, id]);

  const handleInputChange = (field: keyof FormData, value: string) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (errors[field as keyof FormErrors]) {
      setErrors((prev) => ({ ...prev, [field]: undefined }));
    }
  };

  const handleProjectChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const selectedValues = Array.isArray(e.target.value) ? (e.target.value as any) : e.target.value ? [e.target.value] : [];
    setFormData((prev) => ({ ...prev, projectIds: selectedValues }));
  };

  const handleCompetitorChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const selectedValues = Array.isArray(e.target.value) ? (e.target.value as any) : e.target.value ? [e.target.value] : [];
    setFormData((prev) => ({ ...prev, productCompetitorIds: selectedValues }));
  };

  const validateForm = (): boolean => {
    const newErrors: FormErrors = {};

    if (!formData.name.trim()) newErrors.name = "Product name is required";
    if (!formData.group) newErrors.group = "Product group is required";
    if (!formData.type) newErrors.type = "Product type is required";
    if (!formData.packType) newErrors.packType = "Pack type is required";
    if (formData.costSellingPrice && isNaN(Number(formData.costSellingPrice))) newErrors.costSellingPrice = "Must be a valid number";
    if (formData.retailSellingPrice && isNaN(Number(formData.retailSellingPrice))) newErrors.retailSellingPrice = "Must be a valid number";
    if (formData.dealerSellingPrice && isNaN(Number(formData.dealerSellingPrice))) newErrors.dealerSellingPrice = "Must be a valid number";

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleImageUpload = useCallback(async (files: File[] | File) => {
    const fileArray = Array.isArray(files) ? files : [files];
    if (fileArray.length === 0) return;

    const file = fileArray[0];
    if (!file.type.startsWith("image/")) {
      setImageError("Please select an image file");
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      setImageError("Image size must be less than 5MB");
      return;
    }

    setIsUploadingImage(true);
    setImageError("");

    try {
      const fd = new FormData();
      fd.append("image", file);

      const apiUrl = import.meta.env.VITE_API_URL || "http://localhost:4000";
      const token = getToken();

      const response = await axios.post(`${apiUrl}/upload/image`, fd, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "multipart/form-data",
        },
      });

      const canonicalUrl = response.data?.data?.url as string | undefined;
      const signedUrl = response.data?.data?.signedUrl as string | undefined;
      if (canonicalUrl) {
        setFormData((prev) => ({ ...prev, productImageUrl: canonicalUrl }));
        setImageUrl(signedUrl || canonicalUrl);
      } else {
        setImageError("Upload did not return an image URL");
      }
    } catch (error: any) {
      setImageError(error?.response?.data?.message || "Failed to upload image");
    } finally {
      setIsUploadingImage(false);
    }
  }, []);

  const handleImageRemove = async () => {
    const canonical = formData.productImageUrl;
    if (!canonical) {
      setImageUrl("");
      return;
    }

    try {
      const url = new URL(canonical);
      const s3Key = url.pathname.replace(/^\//, "");

      const apiUrl = import.meta.env.VITE_API_URL || "http://localhost:4000";
      const token = getToken();

      await axios.delete(`${apiUrl}/upload/image`, {
        headers: { Authorization: `Bearer ${token}` },
        data: { key: s3Key },
      });

      setImageUrl("");
      setFormData((prev) => ({ ...prev, productImageUrl: "" }));
      setImageError("");
    } catch (error: any) {
      console.error("Failed to delete image from S3", error);
      setImageError("Failed to delete image from S3");
      setImageUrl("");
      setFormData((prev) => ({ ...prev, productImageUrl: "" }));
    }
  };

  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      const files = Array.from(e.dataTransfer.files);
      if (files.length > 0) handleImageUpload(files);
    },
    [handleImageUpload]
  );

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validateForm()) return;

    setLoading(true);
    setSubmitError("");
    setSubmitSuccess("");

    try {
      const apiUrl = import.meta.env.VITE_API_URL || "http://localhost:4000";
      const token = getToken();

      const apiData: any = {
        name: formData.name,
        description: formData.description || undefined,
        sku: formData.sku || undefined,
        barcode: formData.barcode || undefined,
        group: formData.group || undefined,
        type: formData.type || undefined,
        uom: formData.uom || undefined,
        packType: formData.packType || undefined,
        packDimension: formData.packDimension || undefined,
        grossWeightPerUOM: formData.grossWeightPerUOM ? Number(formData.grossWeightPerUOM) : undefined,
        costSellingPrice: formData.costSellingPrice ? Number(formData.costSellingPrice) : undefined,
        retailSellingPrice: formData.retailSellingPrice ? Number(formData.retailSellingPrice) : undefined,
        dealerSellingPrice: formData.dealerSellingPrice ? Number(formData.dealerSellingPrice) : undefined,
        productImageUrl: formData.productImageUrl || undefined,
        projectIds: formData.projectIds && formData.projectIds.length > 0 ? formData.projectIds : undefined,
        companyId: formData.companyId || undefined,
        status: formData.status === "true",
        productLine: formData.productLine || "Own",
        productCompetitor:
          formData.productCompetitorIds && formData.productCompetitorIds.length > 0 ? formData.productCompetitorIds : undefined,
      };

      if (mode === "add") {
        const queryParams = new URLSearchParams();
        if (formData.companyId) queryParams.append("companyId", formData.companyId);

        await axios.post(`${apiUrl}/products?${queryParams.toString()}`, apiData, {
          headers: { Authorization: `Bearer ${token}` },
        });
        setSubmitSuccess("Product created successfully!");
      } else {
        await axios.put(`${apiUrl}/products/${id}`, apiData, {
          headers: { Authorization: `Bearer ${token}` },
        });
        setSubmitSuccess("Product updated successfully!");
      }

      setTimeout(() => {
        const searchParams = new URLSearchParams(window.location.search);
        const projectId = searchParams.get("projectId");
        const companyId = formData.companyId;

        let redirectUrl = "/products";
        if (projectId) redirectUrl += `?projectId=${projectId}`;
        else if (companyId) redirectUrl += `?companyId=${companyId}`;

        navigate(redirectUrl);
      }, 1500);
    } catch (error: any) {
      console.error("Submit failed:", error);
      const message = error.response?.data?.message || error.message || "An error occurred";
      setSubmitError(message);
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    const searchParams = new URLSearchParams(window.location.search);
    const projectId = searchParams.get("projectId");
    const companyId = formData.companyId;

    let redirectUrl = "/products";
    if (projectId) redirectUrl += `?projectId=${projectId}`;
    else if (companyId) redirectUrl += `?companyId=${companyId}`;

    navigate(redirectUrl);
  };

  if (loading && mode === "edit") {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <Lucide icon="Loader2" className="w-8 h-8 animate-spin mx-auto mb-2" />
          <div>Loading product...</div>
        </div>
      </div>
    );
  }

  return (
    <div className="grid grid-cols-12 gap-y-10 gap-x-6">
      <div className="col-span-12">
        <div className="flex flex-col mt-4 md:mt-0 md:h-10 gap-y-3 md:items-center md:flex-row">
          <div className="text-base font-medium group-[.mode--light]:text-white">
            {mode === "add" ? "Add Product" : "Edit Product"}
          </div>
        </div>

        <div className="mt-3.5 grid grid-cols-12 xl:grid-cols-10 gap-y-7 lg:gap-y-10 gap-x-6">
          <div className="relative flex flex-col col-span-12 lg:col-span-9 xl:col-span-8 gap-y-7">
            <form onSubmit={handleSubmit}>
              <div className="flex flex-col p-5 box box--stacked">
                <div className="p-5 border rounded-[0.6rem] border-slate-200/60 dark:border-darkmode-400">
                  <div className="flex items-center pb-5 text-[0.94rem] font-medium border-b border-slate-200/60 dark:border-darkmode-400">
                    <Lucide icon="ChevronDown" className="w-5 h-5 stroke-[1.3] mr-2" />
                    Product Information
                  </div>

                  {submitError && (
                    <Alert variant="outline-danger" className="mt-5">
                      <Lucide icon="AlertTriangle" className="w-6 h-6" />
                      {submitError}
                    </Alert>
                  )}

                  {submitSuccess && (
                    <Alert variant="outline-success" className="mt-5">
                      <Lucide icon="CheckCircle" className="w-6 h-6" />
                      {submitSuccess}
                    </Alert>
                  )}

                  <div className="flex-col block pt-5 mt-5 xl:items-center sm:flex xl:flex-row first:mt-0 first:pt-0">
                    <label className="inline-block mb-2 sm:mb-0 sm:mr-5 sm:text-right xl:w-60 xl:mr-14">
                      <div className="text-left">
                        <div className="flex items-center">
                          <div className="font-medium">Product Image</div>
                        </div>
                        <div className="mt-1.5 xl:mt-3 text-xs leading-relaxed text-slate-500/80 dark:text-slate-400">
                          Upload product image. Drag & drop or click to select.
                        </div>
                      </div>
                    </label>
                    <div className="flex-1 w-full mt-3 xl:mt-0">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center">
                          <div
                            className="relative flex items-center justify-center w-24 h-24 border rounded-full bg-primary/5 border-primary/10 cursor-pointer hover:border-primary/30 transition-colors"
                            onDrop={handleDrop}
                            onDragOver={handleDragOver}
                            onClick={() => fileInputRef.current?.click()}
                          >
                            {imageUrl ? (
                              <img src={imageUrl} alt="Product image" className="w-full h-full object-cover rounded-full" />
                            ) : (
                              <Lucide
                                icon="Image"
                                className="w-[65%] h-[65%] fill-slate-300/70 -mt-1.5 stroke-[0.5] stroke-slate-400/50"
                              />
                            )}

                            <div className="absolute bottom-0 right-0 flex items-center justify-center rounded-full bg-white border-2 border-primary/20 w-7 h-7 hover:bg-primary/5 transition-colors">
                              {isUploadingImage ? (
                                <Lucide icon="Loader2" className="w-3.5 h-3.5 stroke-[1.3] text-primary animate-spin" />
                              ) : (
                                <Lucide icon="Pencil" className="w-3.5 h-3.5 stroke-[1.3] text-slate-500" />
                              )}
                            </div>
                          </div>

                          {imageUrl && (
                            <Button
                              type="button"
                              variant="outline-secondary"
                              size="sm"
                              className="pl-3.5 pr-4 mr-2 h-8 ml-8"
                              onClick={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                handleImageRemove();
                              }}
                              disabled={isUploadingImage}
                            >
                              <Lucide icon="Trash2" className="w-3.5 h-3.5 mr-1.5 stroke-[1.3]" />
                              Remove
                            </Button>
                          )}
                        </div>

                        <div className="flex items-center gap-3">
                          <FormSwitch>
                            <FormSwitch.Label htmlFor="product-status" className="mr-3">
                              Status
                            </FormSwitch.Label>
                            <FormSwitch.Input
                              id="product-status"
                              type="checkbox"
                              checked={formData.status === "true"}
                              onChange={(e) => handleInputChange("status", e.target.checked ? "true" : "false")}
                            />
                          </FormSwitch>
                        </div>
                      </div>

                      <input
                        ref={fileInputRef}
                        type="file"
                        accept="image/*"
                        className="hidden"
                        onChange={(e) => {
                          const f = e.target.files?.[0];
                          if (f) handleImageUpload(f);
                          e.target.value = "";
                        }}
                      />

                      <div className="hidden">
                        <Dropzone
                          getRef={(el) => {
                            dropzoneRef.current = el;
                          }}
                          options={{
                            url: "#",
                            autoProcessQueue: false,
                            acceptedFiles: "image/*",
                            maxFiles: 1,
                            maxFilesize: 5,
                            addRemoveLinks: false,
                            init: function () {
                              this.on("addedfile", (file) => {
                                handleImageUpload([file]);
                              });
                            },
                          }}
                        />
                      </div>

                      {imageError && <div className="mt-2 text-sm text-red-600 dark:text-red-400">{imageError}</div>}
                    </div>
                  </div>

                  <div className="mt-5 grid grid-cols-12 gap-4">
                    <div className="col-span-12 md:col-span-6">
                      <label className="inline-block mb-2">
                        Product Name <span className="text-danger">*</span>
                      </label>
                      <FormInput
                        type="text"
                        placeholder="Enter product name"
                        value={formData.name}
                        onChange={(e) => handleInputChange("name", e.target.value)}
                        className={clsx({ "border-danger": errors.name })}
                      />
                      {errors.name && <div className="mt-2 text-danger text-xs">{errors.name}</div>}
                    </div>

                    <div className="col-span-12 md:col-span-6">
                      <label className="inline-block mb-2">SKU</label>
                      <FormInput
                        type="text"
                        placeholder="Enter SKU"
                        value={formData.sku}
                        onChange={(e) => handleInputChange("sku", e.target.value)}
                        className={clsx({ "border-danger": errors.sku })}
                      />
                      {errors.sku && <div className="mt-2 text-danger text-xs">{errors.sku}</div>}
                    </div>

                    <div className="col-span-12">
                      <label className="inline-block mb-2">Description</label>
                      <FormTextarea
                        placeholder="Enter product description"
                        value={formData.description}
                        onChange={(e) => handleInputChange("description", e.target.value)}
                        rows={3}
                      />
                    </div>

                    <div className="col-span-12 md:col-span-6">
                      <label className="inline-block mb-2">Barcode</label>
                      <FormInput
                        type="text"
                        placeholder="Enter barcode"
                        value={formData.barcode}
                        onChange={(e) => handleInputChange("barcode", e.target.value)}
                      />
                    </div>

                    <div className="col-span-12 md:col-span-6">
                      <label className="inline-block mb-2">
                        Group <span className="text-danger">*</span>
                      </label>
                      <FormSelect
                        value={formData.group}
                        onChange={(e) => handleInputChange("group", e.target.value)}
                        className={clsx({ "border-danger": errors.group })}
                      >
                        <option value="">Select a product group</option>
                        {productGroups.map((group) => (
                          <option key={group.id} value={group.groupName}>
                            {group.groupName}
                          </option>
                        ))}
                      </FormSelect>
                      {errors.group && <div className="mt-2 text-danger text-xs">{errors.group}</div>}
                    </div>

                    <div className="col-span-12 md:col-span-6">
                      <label className="inline-block mb-2">
                        Type <span className="text-danger">*</span>
                      </label>
                      <FormSelect
                        value={formData.type}
                        onChange={(e) => handleInputChange("type", e.target.value)}
                        className={clsx({ "border-danger": errors.type })}
                      >
                        <option value="">Select a product type</option>
                        {productTypes.map((t) => (
                          <option key={t.id} value={t.typeName}>
                            {t.typeName}
                          </option>
                        ))}
                      </FormSelect>
                      {errors.type && <div className="mt-2 text-danger text-xs">{errors.type}</div>}
                    </div>

                    <div className="col-span-12 md:col-span-6">
                      <label className="inline-block mb-2">Unit of Measure</label>
                      <FormInput
                        type="text"
                        placeholder="e.g., kg, pcs, box"
                        value={formData.uom}
                        onChange={(e) => handleInputChange("uom", e.target.value)}
                      />
                    </div>

                    <div className="col-span-12 md:col-span-6">
                      <label className="inline-block mb-2">
                        Pack <span className="text-danger">*</span>
                      </label>
                      <FormSelect
                        value={formData.packType}
                        onChange={(e) => handleInputChange("packType", e.target.value)}
                        className={clsx({ "border-danger": errors.packType })}
                      >
                        <option value="">Select a pack type</option>
                        {packTypes.map((p) => (
                          <option key={p.id} value={p.packName}>
                            {p.packName}
                          </option>
                        ))}
                      </FormSelect>
                      {errors.packType && <div className="mt-2 text-danger text-xs">{errors.packType}</div>}
                    </div>

                    <div className="col-span-12 md:col-span-6">
                      <label className="inline-block mb-2">Pack Dimension</label>
                      <FormInput
                        type="text"
                        placeholder="e.g., 10x10x5 cm"
                        value={formData.packDimension}
                        onChange={(e) => handleInputChange("packDimension", e.target.value)}
                      />
                    </div>

                    <div className="col-span-12 md:col-span-6">
                      <label className="inline-block mb-2">Gross Weight Per UOM</label>
                      <FormInput
                        type="number"
                        step="0.01"
                        placeholder="Enter weight"
                        value={formData.grossWeightPerUOM}
                        onChange={(e) => handleInputChange("grossWeightPerUOM", e.target.value)}
                      />
                    </div>

                    <div className="col-span-12 md:col-span-6">
                      <label className="inline-block mb-2">Cost Price</label>
                      <FormInput
                        type="number"
                        step="0.01"
                        placeholder="0.00"
                        value={formData.costSellingPrice}
                        onChange={(e) => handleInputChange("costSellingPrice", e.target.value)}
                        className={clsx({ "border-danger": errors.costSellingPrice })}
                      />
                      {errors.costSellingPrice && <div className="mt-2 text-danger text-xs">{errors.costSellingPrice}</div>}
                    </div>

                    <div className="col-span-12 md:col-span-6">
                      <label className="inline-block mb-2">Retail Price</label>
                      <FormInput
                        type="number"
                        step="0.01"
                        placeholder="0.00"
                        value={formData.retailSellingPrice}
                        onChange={(e) => handleInputChange("retailSellingPrice", e.target.value)}
                        className={clsx({ "border-danger": errors.retailSellingPrice })}
                      />
                      {errors.retailSellingPrice && <div className="mt-2 text-danger text-xs">{errors.retailSellingPrice}</div>}
                    </div>

                    <div className="col-span-12 md:col-span-6">
                      <label className="inline-block mb-2">Nett Price</label>
                      <FormInput
                        type="number"
                        step="0.01"
                        placeholder="0.00"
                        value={formData.dealerSellingPrice}
                        onChange={(e) => handleInputChange("dealerSellingPrice", e.target.value)}
                        className={clsx({ "border-danger": errors.dealerSellingPrice })}
                      />
                      {errors.dealerSellingPrice && <div className="mt-2 text-danger text-xs">{errors.dealerSellingPrice}</div>}
                    </div>

                    {/* Removed: Assign to Projects, Product Line, Select Product Competitor – not used in StockLedger */}
                  </div>

                  <div className="flex justify-end mt-8 gap-3">
                    <Button type="button" variant="outline-secondary" onClick={handleCancel} disabled={loading}>
                      Cancel
                    </Button>
                    <Button type="submit" variant="primary" disabled={loading}>
                      {loading && <Lucide icon="Loader2" className="w-4 h-4 mr-2 animate-spin" />}
                      {mode === "add" ? "Create Product" : "Update Product"}
                    </Button>
                  </div>
                </div>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
}

